import { formatPrice } from './formatPrice';

/**
 * Format price with currency.
 *
 * Preferred usage: pass a restaurant currency object (from API) so the
 * symbol, position, and decimal places are correct for any currency.
 *
 *   formatCurrency(12.5, currencyObj)            // uses formatPrice internally
 *   formatCurrency(12.5, 'USD', 'en-US')         // legacy Intl.NumberFormat path
 *   formatCurrency(12.5)                          // defaults to $12.50
 *
 * @param {number|string} amount  - Price amount
 * @param {object|string} [currencyOrCode] - Restaurant currency object ({ code, symbol, position })
 *                                           OR an ISO currency code string (default: 'USD')
 * @param {string}        [locale]         - BCP-47 locale, only used with the legacy code-string path
 * @returns {string} Formatted price string
 */
export const formatCurrency = (amount, currencyOrCode, locale) => {
  // Restaurant currency object path (preferred)
  if (currencyOrCode && typeof currencyOrCode === 'object') {
    return formatPrice(amount, currencyOrCode);
  }

  // Legacy code-string path (kept for backward-compat)
  const code = typeof currencyOrCode === 'string' ? currencyOrCode : 'USD';
  const loc = locale || 'en-US';
  const num = parseFloat(amount);
  if (Number.isNaN(num)) return '0.00';

  try {
    return new Intl.NumberFormat(loc, {
      style: 'currency',
      currency: code,
      numberingSystem: 'latn',
      minimumFractionDigits: 2,
      maximumFractionDigits: code === 'OMR' ? 3 : 2,
    }).format(num);
  } catch (e) {
    return `$${num.toFixed(2)}`;
  }
};
